// flash_message/FlashMessage.jsx
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchFlashMessages,
  updateFlashMessage,
  deleteFlashMessage,
} from "../../redux/actions/flashMessageActions";
import theme from "../../components/shared/theme";
import {
  dropdownStyle,
  thStyle,
  tdStyle,
  paginationButtonStyle,
} from "../../components/shared/themeStyles";
import { FiChevronLeft, FiChevronRight, FiEdit2, FiTrash2, FiPlus, FiX, FiBell, FiCalendar, FiClock, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { format } from "date-fns";
import toast from "react-hot-toast";
import CreateFlashMessageModal from "./CreateFlashMessageModal";
import UpdateFlashMessageModal from "./UpdateFlashMessageModal";

const FlashMessage = () => {
  const dispatch = useDispatch();
  const { flashMessages = [], loading, error } = useSelector(
    (state) => state.flashMessages || {}
  );

  // State for filters and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, active, inactive
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // State for modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedFlash, setSelectedFlash] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const itemsPerPage = 10;

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch flash messages on mount
  useEffect(() => {
    dispatch(fetchFlashMessages());
  }, [dispatch]);

  // Filter and sort flash messages
  const filteredMessages = flashMessages
    .filter((msg) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch = 
        msg.title?.toLowerCase().includes(term) ||
        msg.message?.toLowerCase().includes(term);
      
      const matchesStatus = 
        filterStatus === "all" ? true :
        filterStatus === "active" ? msg.is_active === 1 :
        msg.is_active === 0;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === "start_date" || sortField === "end_date" || sortField === "created_at") {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = filteredMessages.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(filteredMessages.length / itemsPerPage);

  // Toggle sort
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Handle create
  const handleCreateClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    dispatch(fetchFlashMessages());
  };

  // Handle edit
  const handleEditClick = (flash) => {
    setSelectedFlash(flash);
    setIsUpdateModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    setIsUpdateModalOpen(false);
    setSelectedFlash(null);
    dispatch(fetchFlashMessages());
  };

  // Handle delete
  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteFlashMessage(deleteConfirmId));
      // toast.success("Flash message deleted successfully");
      setDeleteConfirmId(null);
      dispatch(fetchFlashMessages());
    } catch (error) {
      toast.error(error.message || "Delete failed");
    }
  };

  // Toggle active status
  const handleToggleStatus = async (flash) => {
    try {
      await dispatch(updateFlashMessage({
        id: flash.id,
        title: flash.title,
        message: flash.message,
        start_date: flash.start_date,
        end_date: flash.end_date,
        is_active: flash.is_active === 1 ? 0 : 1
      }));
      toast.success(`Flash message ${flash.is_active === 1 ? 'deactivated' : 'activated'} successfully`);
      dispatch(fetchFlashMessages());
    } catch (error) {
      toast.error("Status update failed");
    }
  };

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "-";
    return format(new Date(dateStr), "dd MMM yyyy");
  };

  // Check if flash is active based on dates
  const isCurrentlyActive = (flash) => {
    const now = new Date();
    const start = new Date(flash.start_date);
    const end = new Date(flash.end_date);
    return flash.is_active === 1 && now >= start && now <= end;
  };

  // Mobile card view
  const renderMobileCard = (flash) => {
    const active = isCurrentlyActive(flash);
    
    return (
      <div
        key={flash.id}
        style={{
          backgroundColor: "#fff",
          borderRadius: theme.radius.lg,
          padding: theme.spacing.md,
          marginBottom: theme.spacing.sm,
          boxShadow: theme.shadow.soft,
          border: `1px solid ${active ? theme.colors.success : "#eee"}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {active && (
          <div style={{
            position: "absolute",
            top: 0,
            right: 0,
            backgroundColor: theme.colors.success,
            color: "#fff",
            padding: "2px 12px",
            fontSize: "0.65rem",
            fontWeight: "bold",
            borderBottomLeftRadius: theme.radius.md,
          }}>
            ACTIVE
          </div>
        )}
        
        <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
          <FiBell color={active ? theme.colors.success : theme.colors.gray} size={18} />
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, flex: 1 }}>
            {flash.title}
          </h3>
          <span style={{
            backgroundColor: flash.is_active ? theme.colors.successLight : theme.colors.grayLight,
            color: flash.is_active ? theme.colors.success : theme.colors.grayDark,
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: "0.65rem",
            fontWeight: 500,
          }}>
            {flash.is_active ? "Active" : "Inactive"}
          </span>
        </div>
        
        <p style={{ fontSize: "0.85rem", color: theme.colors.grayDark, marginBottom: theme.spacing.sm }}>
          {flash.message}
        </p>
        
        <div style={{ display: "flex", gap: theme.spacing.md, fontSize: "0.7rem", color: theme.colors.gray, marginBottom: theme.spacing.sm }}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <FiCalendar size={12} />
            <span>Start: {formatDisplayDate(flash.start_date)}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <FiClock size={12} />
            <span>End: {formatDisplayDate(flash.end_date)}</span>
          </div>
        </div>
        
        <div style={{ display: "flex", justifyContent: "flex-end", gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
          <button
            onClick={() => handleToggleStatus(flash)}
            style={{
              backgroundColor: "transparent",
              border: `1px solid ${flash.is_active ? theme.colors.warning : theme.colors.success}`,
              color: flash.is_active ? theme.colors.warning : theme.colors.success,
              padding: "4px 12px",
              borderRadius: theme.radius.md,
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
          >
            {flash.is_active ? "Deactivate" : "Activate"}
          </button>
          <button
            onClick={() => handleEditClick(flash)}
            style={{
              backgroundColor: theme.colors.primary,
              color: "#fff",
              border: "none",
              padding: "4px 12px",
              borderRadius: theme.radius.md,
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
          <button
            onClick={() => handleDeleteClick(flash.id)}
            style={{
              backgroundColor: theme.colors.danger,
              color: "#fff",
              border: "none",
              padding: "4px 12px",
              borderRadius: theme.radius.md,
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      padding: windowWidth < 768 ? theme.spacing.md : theme.spacing.lg,
      backgroundColor: theme.colors.background,
      minHeight: "100vh",
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing.lg,
        flexWrap: "wrap",
        gap: theme.spacing.sm,
      }}>
        <div>
          <h1 style={{
            fontSize: windowWidth < 768 ? theme.font.size.xl : theme.font.size["2xl"],
            fontWeight: theme.font.weight.bold,
            color: theme.colors.grayDark,
            margin: 0,
          }}>
            Flash Messages
          </h1>
          <p style={{ fontSize: "0.85rem", color: theme.colors.gray, marginTop: "4px" }}>
            Manage system announcements and notifications
          </p>
        </div>
        
        <button
          onClick={handleCreateClick}
          style={{
            backgroundColor: theme.colors.success,
            color: "#fff",
            border: "none",
            borderRadius: theme.radius.md,
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            fontSize: "0.9rem",
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: theme.spacing.xs,
          }}
        >
          <FiPlus size={18} />
          New Flash Message
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: "flex",
        flexDirection: windowWidth < 768 ? "column" : "row",
        flexWrap: "wrap",
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.lg,
        backgroundColor: "#fff",
        padding: theme.spacing.md,
        borderRadius: theme.radius.xl,
        boxShadow: theme.shadow.soft,
      }}>
        <input
          type="text"
          placeholder="Search by title or message..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            ...dropdownStyle,
            flex: 2,
            minWidth: windowWidth < 768 ? "100%" : "250px",
          }}
        />
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            ...dropdownStyle,
            flex: 1,
            minWidth: windowWidth < 768 ? "100%" : "150px",
          }}
        >
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>

        {(searchTerm || filterStatus !== "all") && (
          <button
            onClick={() => {
              setSearchTerm("");
              setFilterStatus("all");
            }}
            style={{
              backgroundColor: theme.colors.grayLight,
              color: theme.colors.grayDark,
              border: "none",
              borderRadius: theme.radius.md,
              padding: theme.spacing.sm,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <FiX size={16} />
            Clear Filters
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: windowWidth < 768 ? "1fr" : "repeat(4, 1fr)",
        gap: theme.spacing.md,
        marginBottom: theme.spacing.lg,
      }}>
        <StatCard
          title="Total Messages"
          value={flashMessages.length}
          icon={<FiBell />}
          color={theme.colors.primary}
        />
        <StatCard
          title="Active Now"
          value={flashMessages.filter(f => isCurrentlyActive(f)).length}
          icon={<FiCheckCircle />}
          color={theme.colors.success}
        />
        <StatCard
          title="Active (Setting)"
          value={flashMessages.filter(f => f.is_active === 1).length}
          icon={<FiBell />}
          color={theme.colors.info}
        />
        <StatCard
          title="Inactive"
          value={flashMessages.filter(f => f.is_active === 0).length}
          icon={<FiXCircle />}
          color={theme.colors.gray}
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={spinnerStyle} />
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div style={{
          textAlign: "center",
          padding: "40px",
          color: theme.colors.danger,
          backgroundColor: "#fff",
          borderRadius: theme.radius.lg,
          boxShadow: theme.shadow.soft,
        }}>
          <FiXCircle size={48} style={{ marginBottom: theme.spacing.md }} />
          <p>Error loading flash messages: {error}</p>
          <button
            onClick={() => dispatch(fetchFlashMessages())}
            style={{
              marginTop: theme.spacing.md,
              backgroundColor: theme.colors.primary,
              color: "#fff",
              border: "none",
              borderRadius: theme.radius.md,
              padding: theme.spacing.sm,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && filteredMessages.length === 0 && (
        <div style={{
          textAlign: "center",
          padding: "60px 40px",
          backgroundColor: "#fff",
          borderRadius: theme.radius.xl,
          boxShadow: theme.shadow.soft,
        }}>
          <FiBell size={64} color={theme.colors.grayLight} style={{ marginBottom: theme.spacing.md }} />
          <h3 style={{ color: theme.colors.grayDark, marginBottom: theme.spacing.sm }}>No Flash Messages Found</h3>
          <p style={{ color: theme.colors.gray, marginBottom: theme.spacing.lg }}>
            {searchTerm || filterStatus !== "all" 
              ? "Try adjusting your filters"
              : "Create your first flash message to get started"}
          </p>
          {(searchTerm || filterStatus !== "all") ? (
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
              }}
              style={buttonStyle(theme.colors.primary)}
            >
              Clear Filters
            </button>
          ) : (
            <button
              onClick={handleCreateClick}
              style={buttonStyle(theme.colors.success)}
            >
              <FiPlus size={18} />
              Create Flash Message
            </button>
          )}
        </div>
      )}

      {/* Mobile Cards View */}
      {!loading && !error && windowWidth < 768 && filteredMessages.length > 0 && (
        <div>
          {currentItems.map((flash) => renderMobileCard(flash))}
        </div>
      )}

      {/* Desktop Table View */}
      {!loading && !error && windowWidth >= 768 && filteredMessages.length > 0 && (
        <div style={{
          backgroundColor: "#fff",
          borderRadius: theme.radius.xl,
          boxShadow: theme.shadow.soft,
          overflowX: "auto",
        }}>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.85rem",
            minWidth: "1000px",
          }}>
            <thead style={{ backgroundColor: theme.colors.grayDark, color: "#fff" }}>
              <tr>
                {["ID", "Title", "Message", "Start Date", "End Date", "Status", "Active Now", "Actions"].map((header, idx) => {
                  const fieldMap = ["id", "title", "message", "start_date", "end_date", "is_active"];
                  const field = fieldMap[idx];
                  return (
                    <th
                      key={header}
                      style={{ ...thStyle, cursor: field ? "pointer" : "default" }}
                      onClick={() => field && toggleSort(field)}
                    >
                      {header}
                      {sortField === field && (sortOrder === "asc" ? " ↑" : " ↓")}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {currentItems.map((flash) => {
                const activeNow = isCurrentlyActive(flash);
                return (
                  <tr
                    key={flash.id}
                    style={{
                      borderBottom: "1px solid #eee",
                      backgroundColor: activeNow ? "#f0fff4" : "#fff",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = activeNow ? "#dcfce7" : "#f8f9fa"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = activeNow ? "#f0fff4" : "#fff"}
                  >
                    <td style={tdStyle}>{flash.id}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{flash.title}</td>
                    <td style={tdStyle}>
                      <div style={{
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {flash.message}
                      </div>
                    </td>
                    <td style={tdStyle}>{formatDisplayDate(flash.start_date)}</td>
                    <td style={tdStyle}>{formatDisplayDate(flash.end_date)}</td>
                    <td style={tdStyle}>
                      <span style={{
                        backgroundColor: flash.is_active ? theme.colors.successLight : theme.colors.grayLight,
                        color: flash.is_active ? theme.colors.success : theme.colors.grayDark,
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}>
                        {flash.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {activeNow ? (
                        <span style={{
                          backgroundColor: theme.colors.success,
                          color: "#fff",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}>
                          Live
                        </span>
                      ) : (
                        <span style={{ color: theme.colors.gray }}>-</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: theme.spacing.xs }}>
                        <button
                          onClick={() => handleToggleStatus(flash)}
                          style={iconButtonStyle(flash.is_active ? theme.colors.warning : theme.colors.success)}
                          title={flash.is_active ? "Deactivate" : "Activate"}
                        >
                          {flash.is_active ? <FiXCircle size={16} /> : <FiCheckCircle size={16} />}
                        </button>
                        <button
                          onClick={() => handleEditClick(flash)}
                          style={iconButtonStyle(theme.colors.primary)}
                          title="Edit"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(flash.id)}
                          style={iconButtonStyle(theme.colors.danger)}
                          title="Delete"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Legend */}
          <div style={{
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.grayLight,
            borderTop: "1px solid #ddd",
            fontSize: "0.7rem",
            display: "flex",
            gap: theme.spacing.md,
            flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div style={{ width: "12px", height: "12px", backgroundColor: "#f0fff4", border: "1px solid #28a745" }} />
              <span>Currently Active</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <FiCheckCircle color={theme.colors.success} size={12} />
              <span>Active Status</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <FiXCircle color={theme.colors.warning} size={12} />
              <span>Inactive Status</span>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && filteredMessages.length > 0 && (
        <div style={{
          display: "flex",
          flexDirection: windowWidth < 768 ? "column" : "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: theme.spacing.sm,
          marginTop: theme.spacing.lg,
          backgroundColor: "#fff",
          padding: theme.spacing.md,
          borderRadius: theme.radius.lg,
          boxShadow: theme.shadow.soft,
        }}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              ...paginationButtonStyle,
              width: windowWidth < 768 ? "100%" : "auto",
            }}
          >
            <FiChevronLeft /> Previous
          </button>
          
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing.md,
            fontSize: "0.85rem",
          }}>
            <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
            <span style={{ color: theme.colors.gray }}>•</span>
            <span>Total: <strong>{filteredMessages.length}</strong></span>
          </div>
          
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              ...paginationButtonStyle,
              width: windowWidth < 768 ? "100%" : "auto",
            }}
          >
            Next <FiChevronRight />
          </button>
        </div>
      )}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <CreateFlashMessageModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Update Modal */}
      {isUpdateModalOpen && selectedFlash && (
        <UpdateFlashMessageModal
          isOpen={isUpdateModalOpen}
          onClose={() => {
            setIsUpdateModalOpen(false);
            setSelectedFlash(null);
          }}
          onSuccess={handleUpdateSuccess}
          flashData={selectedFlash}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div style={modalOverlayStyle}>
          <div style={{
            ...modalContentStyle,
            width: "400px",
            textAlign: "center",
          }}>
            <FiTrash2 size={48} color={theme.colors.danger} style={{ marginBottom: theme.spacing.md }} />
            <h3 style={{ marginBottom: theme.spacing.sm }}>Confirm Delete</h3>
            <p style={{ marginBottom: theme.spacing.lg, color: theme.colors.gray }}>
              Are you sure you want to delete this flash message? This action cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: theme.spacing.md }}>
              <button
                onClick={() => setDeleteConfirmId(null)}
                style={{
                  ...buttonStyle(theme.colors.gray),
                  backgroundColor: "transparent",
                  color: theme.colors.grayDark,
                  border: `1px solid ${theme.colors.gray}`,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={buttonStyle(theme.colors.danger)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Helper Components
const StatCard = ({ title, value, icon, color }) => (
  <div style={{
    backgroundColor: "#fff",
    borderRadius: theme.radius.lg,
    padding: theme.spacing.md,
    boxShadow: theme.shadow.soft,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.md,
  }}>
    <div style={{
      backgroundColor: `${color}20`,
      color: color,
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.5rem",
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: "0.85rem", color: theme.colors.gray }}>{title}</div>
      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: color }}>{value}</div>
    </div>
  </div>
);

// Styles
const spinnerStyle = {
  width: "40px",
  height: "40px",
  border: `3px solid ${theme.colors.grayLight}`,
  borderTopColor: theme.colors.primary,
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
  margin: "0 auto",
};

const buttonStyle = (bgColor) => ({
  backgroundColor: bgColor,
  color: "#fff",
  border: "none",
  borderRadius: theme.radius.md,
  padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
  fontSize: "0.9rem",
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing.xs,
});

const iconButtonStyle = (color) => ({
  backgroundColor: "transparent",
  color: color,
  border: `1px solid ${color}`,
  borderRadius: theme.radius.sm,
  padding: "6px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s",
});

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: "#fff",
  borderRadius: theme.radius.xl,
  padding: theme.spacing.lg,
  boxShadow: theme.shadow.lg,
  maxHeight: "90vh",
  overflowY: "auto",
};

export default FlashMessage;